import { Box, LinearProgress, Typography, IconButton, Collapse, Paper, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { useGlobalProgressBarStore } from '@/store/ui/progress-bar.store';
import { useProgressPolling } from '@/shared/hooks/useProgressPolling';
import { useUiStore } from '@/store/ui/ui.store';

const SIDE_NAV_WIDTH = 280;
const TOP_NAV_HEIGHT = 64;

export const GlobalProgressBar = () => {
  const currentTask = useGlobalProgressBarStore(s => s.currentTask);
  const progressState = useGlobalProgressBarStore(s => s.progressState);
  const isVisible = useGlobalProgressBarStore(s => s.isVisible);
  const clearTask = useGlobalProgressBarStore(s => s.clearTask);
  const openNav = useUiStore(s => s.openNav);

  // Hook para polling automático
  useProgressPolling({ enabled: !!currentTask });

  // Si no hay tarea o no es visible, no renderizar
  if (!currentTask || !progressState || !isVisible) {
    return null;
  }

  const { estado, progreso, total, porcentaje, descripcion, error } = progressState;

  // Detectar si estamos en fase de finalización
  const isFinalizing = porcentaje >= 90 ||
    descripcion.toLowerCase().includes('pdf') ||
    descripcion.toLowerCase().includes('notificacion') ||
    descripcion.toLowerCase().includes('finalizando') ||
    descripcion.toLowerCase().includes('guardando ingreso');

  // Obtener el título según el estado y fase
  const getStatusTitle = () => {
    if (estado === 'COMPLETADO') return 'Procesamiento completado';
    if (estado === 'ERROR') return 'Error en el procesamiento';
    if (estado === 'PENDIENTE') return 'Procesamiento pendiente';

    // Estado PROCESANDO - mostrar fase específica
    if (descripcion.toLowerCase().includes('pdf')) {
      return 'Generando documento PDF...';
    }
    if (descripcion.toLowerCase().includes('notificacion')) {
      return 'Enviando notificaciones...';
    }
    if (descripcion.toLowerCase().includes('guardando ingreso')) {
      return 'Guardando ingreso en base de datos...';
    }
    if (isFinalizing) {
      return 'Finalizando proceso...';
    }
    return 'Procesando series de inventario';
  };

  // Determinar el color según el estado
  const getProgressColor = () => {
    switch (estado) {
      case 'COMPLETADO':
        return 'success';
      case 'ERROR':
        return 'error';
      case 'PROCESANDO':
        return 'primary';
      default:
        return 'inherit';
    }
  };

  // Determinar el icono según el estado
  const getStatusIcon = () => {
    switch (estado) {
      case 'COMPLETADO':
        return <CheckCircleIcon sx={{ color: 'success.main', mr: 1 }} />;
      case 'ERROR':
        return <ErrorIcon sx={{ color: 'error.main', mr: 1 }} />;
      case 'PROCESANDO':
        // Mostrar spinner cuando está finalizando
        if (isFinalizing) {
          return <CircularProgress size={20} sx={{ mr: 1 }} />;
        }
        return null;
      default:
        return null;
    }
  };

  return (
    <Collapse in={isVisible}>
      <Box
        sx={{
          position: 'fixed',
          top: TOP_NAV_HEIGHT + 8,
          left: {
            xs: '50%',
            lg: openNav ? `calc(50% + ${SIDE_NAV_WIDTH / 2}px)` : '50%',
          },
          transform: 'translateX(-50%)',
          width: {
            xs: '90%',
            sm: '80%',
            md: '70%',
            lg: '60%',
            xl: '50%',
          },
          maxWidth: 800,
          zIndex: 1300,
          transition: 'left 0.3s ease',
        }}
      >
        <Paper
          elevation={4}
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ px: 2.5, py: 1.5 }}>
          {/* Header con información general */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
              {getStatusIcon()}
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.875rem' }}>
                  {getStatusTitle()}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                  Ingreso: {currentTask.codigo}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.8rem' }}>
                  {progreso.toLocaleString()} / {total.toLocaleString()}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                  series
                </Typography>
              </Box>
              <Typography variant="body1" sx={{ fontWeight: 700, color: 'primary.main', minWidth: 50, fontSize: '1.1rem' }}>
                {porcentaje}%
              </Typography>
              <IconButton
                size="small"
                onClick={clearTask}
                sx={{ color: 'text.secondary', ml: 0.5 }}
                aria-label="Cerrar barra de progreso"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          {/* Barra de progreso */}
          <Box sx={{ mb: 0.5 }}>
            <LinearProgress
              variant="determinate"
              value={porcentaje}
              color={getProgressColor()}
              sx={{
                height: 6,
                borderRadius: 3,
                bgcolor: 'action.hover',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 3,
                  transition: 'transform 0.5s ease-in-out',
                },
              }}
            />
          </Box>

          {/* Descripción del progreso y tiempo estimado */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {descripcion && (
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                {descripcion}
              </Typography>
            )}

            {/* Tiempo estimado (opcional) */}
            {estado === 'PROCESANDO' && (
              <TimeEstimate
                progreso={progreso}
                total={total}
                startTime={currentTask.startTime}
                porcentaje={porcentaje}
                descripcion={descripcion}
              />
            )}
          </Box>

          {/* Mensaje de error si existe */}
          {error && estado === 'ERROR' && (
            <Typography variant="caption" sx={{ color: 'error.main', display: 'block', mt: 0.5, fontSize: '0.7rem' }}>
              Error: {error}
            </Typography>
          )}
        </Box>
        </Paper>
      </Box>
    </Collapse>
  );
};

// Componente para mostrar tiempo estimado
interface TimeEstimateProps {
  progreso: number;
  total: number;
  startTime: number;
  porcentaje: number;
  descripcion: string;
}

const TimeEstimate = ({ progreso, total, startTime, porcentaje, descripcion }: TimeEstimateProps) => {
  const elapsed = Date.now() - startTime;

  // Detectar si estamos en fase de finalización (PDF, notificaciones, etc.)
  const isFinalizing = porcentaje >= 90 ||
    descripcion.toLowerCase().includes('pdf') ||
    descripcion.toLowerCase().includes('notificacion') ||
    descripcion.toLowerCase().includes('finalizando') ||
    descripcion.toLowerCase().includes('guardando ingreso');

  // Formatear tiempo transcurrido
  const formatElapsedTime = (ms: number) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      return `${hours}h ${minutes % 60}m transcurridos`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s transcurridos`;
    } else {
      return `${seconds}s transcurridos`;
    }
  };

  // Si estamos finalizando, mostrar tiempo transcurrido y estado
  if (isFinalizing) {
    return (
      <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic', fontSize: '0.7rem' }}>
        Finalizando... ({formatElapsedTime(elapsed)})
      </Typography>
    );
  }

  // Calcular tiempo restante solo si tenemos suficientes datos
  if (progreso < 10) {
    return (
      <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic', fontSize: '0.7rem' }}>
        Calculando tiempo restante...
      </Typography>
    );
  }

  const rate = progreso / elapsed; // series por milisegundo
  const remaining = total - progreso;
  const estimatedMs = remaining / rate;

  const formatTime = (ms: number) => {
    if (!ms || ms <= 0 || !isFinite(ms)) return 'Casi listo...';

    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      return `~${hours}h ${minutes % 60}m restantes`;
    } else if (minutes > 0) {
      return `~${minutes}m ${seconds % 60}s restantes`;
    } else if (seconds > 5) {
      return `~${seconds}s restantes`;
    } else {
      return 'Casi listo...';
    }
  };

  return (
    <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic', fontSize: '0.7rem' }}>
      {formatTime(estimatedMs)}
    </Typography>
  );
};

export default GlobalProgressBar;
